"use client";

import { useQuery } from "@apollo/client";
import SvgWithOutData from "components/core/SvgWithOutData/SvgWithOutData";
import { gqlVendor } from "gql";
import { Link as LinkIcon } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { ExploreSideCardHeaderV2 } from "components/ui/v2/explore/ExploreSideCardHeaderV2";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "shadcn/components/ui/accordion";
import { Card as CardV2 } from "components/ui/v2/card";
import { IVendorLinks } from "types/Vendor.types";
import {
  isCacheValid,
  useExploreWidgetsCacheRefresh,
  useExploreWidgetsStore
} from "stores/explore/useExploreWidgetsStore";
import { faToLucideName } from "utils/iconMapping";
import { STATIC_LUCIDE_ICONS } from "utils/static-lucide-registry";

interface ListExternalLinkProps {
  readonly variant?: "default" | "v2";
}

export default function ListExternalLink(props: ListExternalLinkProps) {
  const { variant = "default" } = props;
  const { t } = useTranslation("common");
  const [data, setData] = useState<IVendorLinks[]>([]);
  const { externalLinks, setExternalLinks, hasHydrated } =
    useExploreWidgetsStore();
  useExploreWidgetsCacheRefresh();
  const hasValidCache = isCacheValid(externalLinks.lastFetched);

  const { loading } = useQuery(gqlVendor.queries.VENDOR_LINKS, {
    fetchPolicy: "network-only",
    skip: !hasHydrated || hasValidCache,
    notifyOnNetworkStatusChange: true,
    onCompleted: (queryData) => {
      if (queryData?.getVendorLinks) {
        setExternalLinks(queryData.getVendorLinks);
      }
    }
  });

  useEffect(() => {
    if (!externalLinks.data) return;

    // Copia los datos originales para no mutar directamente
    const originalLinks = [...externalLinks.data];

    // Ordenar los datos por el campo 'order', si está presente
    // @ts-ignore
    const sortedData = originalLinks.toSorted((a, b) => {
      if (a.order && b.order) {
        return a.order - b.order; // Ordenar de menor a mayor
      } else if (a.order) {
        return -1; // 'a' tiene 'order', va primero
      } else if (b.order) {
        return 1; // 'b' tiene 'order', va primero
      } else {
        return 0; // Ninguno tiene 'order', no se cambia el orden
      }
    });

    // Agregar estado isDisabled a cada enlace
    const linksWithDisabledState = sortedData.map((link) => ({
      ...link,
      isDisabled: true
    }));

    setData(linksWithDisabledState);
  }, [externalLinks.data]);

  if (loading && !externalLinks.data) {
    return (
      <CardV2
        size="full"
        variant="default"
        tone="default"
        padding="none"
        shadow="subtle"
        className="w-full max-w-full"
      >
        <div className="px-4 py-3" role="status" aria-label="Loading links">
          <div className="mb-4 flex items-center gap-2">
            <div className="relative h-4 w-4 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
            <div className="relative h-3 w-28 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
            <div className="relative h-3 w-5/6 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
            <div className="relative h-3 w-4/6 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
          </div>
        </div>
      </CardV2>
    );
  }

  return (
    <CardV2
      size="full"
      variant="default"
      tone="default"
      padding="none"
      shadow="subtle"
      className="w-full max-w-full"
    >
      <Accordion
        type="single"
        collapsible={true}
        defaultValue="item-1" // Default AccordionItem Open
      >
        <AccordionItem value="item-1" className="rounded-xl border-b-0">
          <AccordionTrigger className="px-4 py-1.5">
            <ExploreSideCardHeaderV2
              title={t("Enlaces directos")}
              icon={
                variant === "v2" ? (
                  <LinkIcon size={14} strokeWidth={1.1} color="#0073FF" />
                ) : null
              }
              className="w-full pr-1"
            />
          </AccordionTrigger>
          {data?.map((link, index) => (
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              key={link.id}
              className={`${index === data.length - 1 ? "rounded-b-2xl" : ""}`}
            >
              <AccordionContent
                className={`flex h-10 w-full place-items-center items-center gap-1 border-b-[1px] bg-white px-4 pb-0 align-middle hover:bg-accent dark:border-gray-600 dark:bg-black ${
                  index === data.length - 1
                    ? "rounded-b-xl border-b-0 pb-0"
                    : ""
                }`}
              >
                {(() => {
                  const lucideName = faToLucideName(String(link.icon || "link"));
                  const LinkItemIcon =
                    STATIC_LUCIDE_ICONS[lucideName] ?? LinkIcon;
                  return <LinkItemIcon className="h-4 w-4 min-w-6" />;
                })()}
                <span className="line-clamp-1 text-ellipsis text-[11px] font-semibold leading-tight">
                  {link.name}
                </span>
              </AccordionContent>
            </a>
          ))}
        </AccordionItem>
      </Accordion>

      {
        // * if there are no links
        data?.length === 0 && (
          <div className="p-4">
            <p className="py-4 text-center text-gray-500 dark:bg-gray-900">
              {t("No hay enlaces disponibles")}
            </p>
            <SvgWithOutData />
          </div>
        )
      }
    </CardV2>
  );
}
